/**
 * AdminSettingsPowerBIReportInstance - Container component for PBI Report Instance CRUD operations.
 *
 * This component orchestrates the management of Power BI Report Instance records,
 * providing search, pagination, sorting, and CRUD modal flows. It follows the pattern
 * established by AdminSettingsPowerBIReport.tsx for consistent UX and code organization.
 *
 * Features:
 * - Paginated table of report instances
 * - Sortable columns with ascending/descending direction
 * - Add new instance modal
 * - Edit existing instance modal
 * - Delete confirmation dialog
 * - Success notifications for CRUD operations
 * - RTK Query mutation for efficient data fetching
 *
 * @module AdminSettings/AdminSettingsPowerBIReportInstance
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Box, Button } from '@mui/material'
import {
  PbiReportDefinitionViewModel,
  PbiReportInstanceViewModel,
} from '@rsmus/ecp-userservice'
import { useTranslation } from 'react-i18next'
import { RsmPaginationPageSizes } from '../../rsmCoreComponents/components/RsmPagination/RsmPagination'
import {
  PBI_INSTANCE_COLUMNS,
  mapPbiInstanceSortKey,
} from '../../utils/constants/constants'
import ConfirmRemoveReportInstanceDialog from './ConfirmRemoveReportInstanceDialog'
import AdminPbiReportInstanceResults from './AdminPbiReportInstanceResults'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert'
import api from '../../api'
import PowerBIReportInstanceModal from './PowerBIReportInstanceModal'
import {
  useUpdateReportInstanceSearchResultsMutation,
  useUpdateReportSearchResultsMutation,
  resetInstanceSearch,
} from '../../store/adminSettings/PbiReportSearchService'

/**
 * Extended AdminReportDataViewModel with isClientLevelAuthorization property.
 * Used for parent report definition selection in the instance modal.
 */
type ExtendedAdminReportDataViewModel = PbiReportDefinitionViewModel

type PbiReportInstanceRow = PbiReportInstanceViewModel & {
  reportDefinitionName?: string | null
}

// Default pagination and sorting values
const DEFAULT_PBI_REPORT_INSTANCE_PAGE_NUMBER = 1
const DEFAULT_PBI_REPORT_INSTANCE_PAGE_SIZE = 10 as RsmPaginationPageSizes
const DEFAULT_PBI_REPORT_INSTANCE_SORT_COLUMN =
  PBI_INSTANCE_COLUMNS.DISPLAY_NAME
const DEFAULT_PBI_REPORT_INSTANCE_SORT_DIRECTION: 'asc' | 'desc' = 'asc'

/**
 * AdminSettingsPowerBIReportInstance - Main container for instance management.
 *
 * @returns A React component rendering the instance management interface
 */
const AdminSettingsPowerBIReportInstance = () => {
  const { t } = useTranslation()

  // RTK Query mutation for fetching instances
  const [updateReportInstanceSearchResults] =
    useUpdateReportInstanceSearchResultsMutation()

  // RTK Query mutation for fetching report definitions (for dropdown)
  const [updateReportSearchResults] = useUpdateReportSearchResultsMutation()

  // Modal state management
  const [openAddInstance, setOpenAddInstance] = useState(false)
  const [openEditInstance, setOpenEditInstance] = useState(false)
  const [openRemoveConfirm, setOpenRemoveConfirm] = useState(false)
  const [selectedInstance, setSelectedInstance] =
    useState<PbiReportInstanceViewModel | null>(null)

  // Success notification state
  const [isSaveInstanceSuccess, setIsSaveInstanceSuccess] = useState(false)
  const [isEditSaveInstanceSuccess, setIsEditSaveInstanceSuccess] =
    useState(false)
  const [isRemoveSaveInstanceSuccess, setIsRemoveSaveInstanceSuccess] =
    useState(false)

  // Data state
  const [instancesData, setInstancesData] = useState<{
    data: PbiReportInstanceRow[]
    totalRows: number
  }>({ data: [], totalRows: 0 })
  const [isInstanceError, setIsInstanceError] = useState(false)
  const [isMounted, setIsMounted] = useState(false)

  // Pagination state
  const [pageNumber, setPageNumber] = useState(
    DEFAULT_PBI_REPORT_INSTANCE_PAGE_NUMBER,
  )
  const [pageSize, setPageSize] = useState(
    DEFAULT_PBI_REPORT_INSTANCE_PAGE_SIZE,
  )

  // Sorting state - type as string to allow flexible sort key mapping
  const [sortColumn, setSortColumn] = useState<string>(
    DEFAULT_PBI_REPORT_INSTANCE_SORT_COLUMN,
  )
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    DEFAULT_PBI_REPORT_INSTANCE_SORT_DIRECTION,
  )

  // Report definitions for modal dropdown
  const [reportDefinitions, setReportDefinitions] = useState<
    ExtendedAdminReportDataViewModel[]
  >([])

  /**
   * Fetches instances with current pagination and sorting parameters.
   */
  const fetchInstances = useCallback(async () => {
    const filter = {
      pageNumber,
      pageSize,
      sortBy: sortColumn,
      isSortAscending: sortDirection === 'asc',
    }

    try {
      const result = await updateReportInstanceSearchResults(filter).unwrap()

      setInstancesData({
        data: result.data as PbiReportInstanceViewModel[],
        totalRows: result.totalRows,
      })
      setIsInstanceError(false)
    } catch {
      // Error handled via isInstanceError state
      setIsInstanceError(true)
      setInstancesData({ data: [], totalRows: 0 })
    } finally {
      setIsMounted(true)
    }
  }, [
    pageNumber,
    pageSize,
    sortColumn,
    sortDirection,
    updateReportInstanceSearchResults,
  ])

  /**
   * Fetches report definitions for the modal dropdown.
   * Uses RTK Query mutation with flexible filter to avoid type issues.
   */
  const fetchReportDefinitions = useCallback(async () => {
    try {
      const result = await updateReportSearchResults({
        pageNumber: 1,
        pageSize: 1000, // Get all definitions for dropdown
        sortBy: 'ReportName',
        isSortAscending: true,
      }).unwrap()
      setReportDefinitions(result.data as ExtendedAdminReportDataViewModel[])
    } catch {
      // Silent fail for dropdown population - user can retry via refresh
    }
  }, [updateReportSearchResults])

  // Initial fetch on mount
  useEffect(() => {
    fetchInstances()
  }, [fetchInstances])
  useEffect(() => {
    fetchReportDefinitions()
    // Cleanup on unmount
    return () => {
      resetInstanceSearch()
    }
  }, [fetchInstances, fetchReportDefinitions])

  /**
   * Resets pagination and sorting to defaults.
   * Note: Currently unused but kept for potential future use (e.g., search reset).
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const resetInstanceSearchState = useCallback(() => {
    setPageNumber(DEFAULT_PBI_REPORT_INSTANCE_PAGE_NUMBER)
    setPageSize(DEFAULT_PBI_REPORT_INSTANCE_PAGE_SIZE)
    setSortColumn(DEFAULT_PBI_REPORT_INSTANCE_SORT_COLUMN)
    setSortDirection(DEFAULT_PBI_REPORT_INSTANCE_SORT_DIRECTION)
  }, [])

  /**
   * Handles sorting changes from the table.
   */
  const handleSorting = useCallback(
    (columnName: string, direction: 'asc' | 'desc') => {
      const mappedColumn = mapPbiInstanceSortKey(columnName)
      setSortColumn(mappedColumn)
      setSortDirection(direction)
    },
    [],
  )

  /**
   * Handles page changes from pagination.
   */
  const handlePageChange = useCallback(
    (newPageNumber: number, newPageSize: RsmPaginationPageSizes) => {
      setPageNumber(newPageNumber)
      setPageSize(newPageSize)
    },
    [],
  )

  /**
   * Opens the add instance modal.
   */
  const handleOpenAddModal = useCallback(() => {
    setSelectedInstance(null)
    setOpenAddInstance(true)
  }, [])

  /**
   * Handles opening the edit modal for an instance.
   */
  const handleEditInstance = useCallback(
    (instance: PbiReportInstanceViewModel) => {
      setSelectedInstance(instance)
      setOpenEditInstance(true)
    },
    [],
  )

  /**
   * Handles saving a new instance.
   * The instance parameter is received from the modal but not used directly
   * since we refresh the entire list after saving.
   */
  const handleAddNewInstance = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_instance: PbiReportInstanceViewModel) => {
      setOpenAddInstance(false)
      setIsSaveInstanceSuccess(true)
      // Refresh the data
      fetchInstances()
    },
    [fetchInstances],
  )

  /**
   * Handles updating an existing instance.
   * The instance parameter is received from the modal but not used directly
   * since we refresh the entire list after updating.
   */
  const handleUpdateInstance = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_instance: PbiReportInstanceViewModel) => {
      setOpenEditInstance(false)
      setIsEditSaveInstanceSuccess(true)
      // Refresh the data
      fetchInstances()
    },
    [fetchInstances],
  )

  /**
   * Opens the remove confirmation dialog.
   */
  const handleAskRemoveInstance = useCallback(
    (instance: PbiReportInstanceViewModel) => {
      setSelectedInstance(instance)
      setOpenRemoveConfirm(true)
    },
    [],
  )

  /**
   * Handles confirming instance removal.
   */
  const confirmRemoveInstance = useCallback(async () => {
    if (!selectedInstance?.id) return

    try {
      // Use type assertion to access the potentially not-yet-generated API method
      if (!selectedInstance) return
      await api.user.report_RemoveReportInstance(selectedInstance.id, undefined)
      // If method not available, still update local state optimistically

      // Update local state
      setInstancesData((prev) => ({
        data: prev.data.filter((item) => item.id !== selectedInstance.id),
        totalRows: Math.max(0, prev.totalRows - 1),
      }))
      setIsRemoveSaveInstanceSuccess(true)
    } catch {
      // Error handled via isInstanceError state
      setIsInstanceError(true)
    } finally {
      setOpenRemoveConfirm(false)
      setSelectedInstance(null)
    }
  }, [selectedInstance])

  /**
   * Handles closing modals.
   */
  const handleCloseAddModal = useCallback(() => {
    setOpenAddInstance(false)
    setSelectedInstance(null)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setOpenEditInstance(false)
    setSelectedInstance(null)
  }, [])

  const handleCloseRemoveDialog = useCallback(() => {
    setOpenRemoveConfirm(false)
    setSelectedInstance(null)
  }, [])

  /**
   * Gets the parent report definition name for the selected instance.
   */
  const getReportDefinitionName = (): string => {
    if (!selectedInstance?.pbiReportDefinitionId) return ''
    const def = reportDefinitions.find(
      (d) => d.id === selectedInstance.pbiReportDefinitionId,
    )
    return def?.reportName ?? ''
  }

  const instancesDataWithNames = useMemo(() => {
    const defNameById = new Map<number, string>(
      reportDefinitions.map((d) => [d.id, d.reportName ?? '']),
    )

    return {
      totalRows: instancesData.totalRows,
      data: instancesData.data.map((i: any) => ({
        ...i,
        reportDefinitionName:
          defNameById.get(i.pbiReportDefinitionId ?? 0) ?? '',
      })),
    }
  }, [instancesData, reportDefinitions])

  return (
    <Box>
      {/* Add New Instance Button */}
      <Box sx={{ marginBottom: '1rem' }}>
        <Button
          variant="contained"
          onClick={handleOpenAddModal}
          data-testid="Btn_AdminSettingsForm_AddNewPowerBIReportInstance">
          {t('PbiReportInstance.AddNewReportInstance')}
        </Button>
      </Box>

      {/* Instance Results (Table + Pagination) */}
      <AdminPbiReportInstanceResults
        instancesData={instancesDataWithNames}
        isInstanceError={isInstanceError}
        isMounted={isMounted}
        pageNumber={pageNumber}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSorting={handleSorting}
        onEditReportInstance={handleEditInstance}
        onRemoveReportInstance={handleAskRemoveInstance}
      />

      {/* Add Instance Modal */}
      <PowerBIReportInstanceModal
        open={openAddInstance}
        onClose={handleCloseAddModal}
        onSave={handleAddNewInstance}
        mode="create"
        reportDefinitions={reportDefinitions}
      />

      {/* Edit Instance Modal */}
      <PowerBIReportInstanceModal
        open={openEditInstance}
        onClose={handleCloseEditModal}
        onUpdate={handleUpdateInstance}
        onRemove={(instanceId) => {
          setOpenEditInstance(false)
          setSelectedInstance({ id: instanceId } as PbiReportInstanceViewModel)
          setOpenRemoveConfirm(true)
        }}
        mode="edit"
        initialData={selectedInstance ?? undefined}
        reportDefinitions={reportDefinitions}
      />

      {/* Remove Confirmation Dialog */}
      <ConfirmRemoveReportInstanceDialog
        open={openRemoveConfirm}
        onClose={handleCloseRemoveDialog}
        onConfirm={confirmRemoveInstance}
        instanceDisplayName={selectedInstance?.displayName}
        reportDefinitionName={getReportDefinitionName()}
      />

      {/* Success Notifications */}
      <CustomSuccessAlert
        open={isSaveInstanceSuccess}
        close={() => setIsSaveInstanceSuccess(false)}
        header={t(
          'AdminSettingsForm.PbiReport.ReportInstanceSaveSuccess.Title',
        )}
        message={t(
          'AdminSettingsForm.PbiReport.ReportInstanceSaveSuccess.Message',
        )}
        testId="PbiInstance_InstanceSave_Success"
      />

      <CustomSuccessAlert
        open={isEditSaveInstanceSuccess}
        close={() => setIsEditSaveInstanceSuccess(false)}
        header={t(
          'AdminSettingsForm.PbiReport.EditReportInstanceSaveSuccess.Title',
        )}
        message={t(
          'AdminSettingsForm.PbiReport.EditReportInstanceSaveSuccess.Message',
        )}
        testId="PbiInstance_EditInstanceSave_Success"
      />

      <CustomSuccessAlert
        open={isRemoveSaveInstanceSuccess}
        close={() => setIsRemoveSaveInstanceSuccess(false)}
        header={t(
          'AdminSettingsForm.PbiReport.RemoveReportInstanceSaveSuccess.Title',
        )}
        message={t(
          'AdminSettingsForm.PbiReport.RemoveReportInstanceSaveSuccess.Message',
        )}
        testId="PbiInstance_RemoveInstanceSave_Success"
      />
    </Box>
  )
}

export default AdminSettingsPowerBIReportInstance
